import { ComponentType, DiagramComponentInstance, DiagramConnection } from 'models';

type Rotation = 0 | 90 | 180 | 270;

export interface IconTransform {
    css: string;
    upright?: string;
}

interface IconOrientation {
    rotation: Rotation;
    mirrored?: 'horizontal' | 'vertical';
}

const ROTATABLE_COMPONENT_TYPES: ComponentType[] = [
    'breaker',
    'diode',
    'disconnect',
    'capacitor',
    'transformer',
    'contactor',
    'fuse',
];

const ORIENTATIONS: Partial<Record<DiagramConnection['from']['edge'], IconOrientation>> = {
    bottom: { rotation: 270 },
    left: { rotation: 0 },
    top: { rotation: 90, mirrored: 'vertical' },
    right: { rotation: 0, mirrored: 'horizontal' },
};

const INVERSE_ROTATION: Record<Rotation, number> = { 0: 0, 90: -90, 180: 180, 270: 90 };

export const getIconTransform = (
    componentInstance: DiagramComponentInstance,
    connections: DiagramConnection[],
    viewBox?: string,
): IconTransform | undefined => {
    if (!ROTATABLE_COMPONENT_TYPES.includes(componentInstance.componentType)) {
        return undefined;
    }

    const edge = connections
        .flatMap((connection) => [connection.from, connection.to])
        .filter((endpoint) => endpoint.componentInstanceId === componentInstance.id && endpoint.port === 0)
        .pop()?.edge;

    if (!edge) {
        return undefined;
    }

    const orientation = ORIENTATIONS[edge];

    if (!orientation) {
        return undefined;
    }

    return { css: getCssTransform(orientation), upright: getUprightTransform(orientation, viewBox) };
};

const getCssTransform = ({ rotation, mirrored }: IconOrientation): string => {
    const transforms: string[] = [];

    if (rotation) {
        transforms.push(`rotate(${rotation}deg)`);
    }

    if (mirrored == 'horizontal') {
        transforms.push(`scaleX(-1)`);
    }

    if (mirrored == 'vertical') {
        transforms.push(`scaleY(-1)`);
    }

    return transforms.join(' ');
};

const getMirrorTransform = (
    mirrored: IconOrientation['mirrored'],
    centerX: number,
    centerY: number,
): string | undefined => {
    if (mirrored == 'horizontal') {
        return `translate(${centerX * 2} 0) scale(-1 1)`;
    }

    if (mirrored == 'vertical') {
        return `translate(0 ${centerY * 2}) scale(1 -1)`;
    }

    return undefined;
};

const getUprightTransform = ({ rotation, mirrored }: IconOrientation, viewBox?: string): string | undefined => {
    if (!viewBox || (!rotation && !mirrored)) {
        return undefined;
    }

    const [minX, minY, width, height] = viewBox.split(/[\s,]+/).map(Number);

    if (![minX, minY, width, height].every(Number.isFinite)) {
        return undefined;
    }

    const centerX = minX + width / 2;
    const centerY = minY + height / 2;

    return [
        getMirrorTransform(mirrored, centerX, centerY),
        rotation ? `rotate(${INVERSE_ROTATION[rotation]} ${centerX} ${centerY})` : undefined,
    ]
        .filter(Boolean)
        .join(' ');
};
